import { ReactNode, useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import ActionPendingIcon from '@assets/images/action-pending.svg?react'
import cn from 'classnames'
import genericSs from '../../assets/styles/generic.module.scss'
import { handleStopPropagation } from '../../helpers/helpers'
import Button from '../Common/Button'
import Text from '../Common/Text'
import Modal from './../Common/Modal'
import styles from './WarningModal.module.scss'

interface Props {
  title?: string | ReactNode
  warningMessage: ReactNode
  className?: string
  // Set when a previous confirm attempt was refused (e.g. a permission or
  // business-rule error from the server) — rendered below warningMessage,
  // in error styling, while keeping the modal open for the user to read it.
  errorMessage?: string | null
  onConfirm?: () => void
  confirmText?: string
  disabled?: boolean
  onClose?: () => void
  onCancel: () => void
  cancelText?: string
  isLoading?: boolean
  isLoadingCancel?: boolean
  inputComponent?: ReactNode
}

const WarningModal = ({
  title = 'Are you sure?',
  warningMessage,
  errorMessage,
  onConfirm,
  confirmText = 'Yes',
  disabled,
  onClose,
  onCancel,
  cancelText = 'No',
  isLoading,
  isLoadingCancel,
  inputComponent,
  className,
}: Props) => {
  const isBusy = useMemo(() => Boolean(isLoading || isLoadingCancel), [isLoading, isLoadingCancel])

  const handleModalCancel = useCallback(() => {
    if (isBusy) return
    if (onClose) return onClose()
    return onCancel()
  }, [isBusy, onClose, onCancel])

  return (
    <Modal open title="" onCancel={handleModalCancel} hideCloseIcon={isBusy}>
      <Box
        className={cn(styles.modalBody, className, { [styles.hideButtons]: !onConfirm })}
        onClick={handleStopPropagation}
      >
        <Grid container className={styles.centeredGrid}>
          <Grid
            container
            className={styles.centeredGrid}
            size={{
              xs: 12,
              lg: 12,
            }}
          >
            <Grid
              className={styles.centeredItem}
              size={{
                xs: 12,
                lg: 12,
              }}
            >
              <ActionPendingIcon />
            </Grid>
          </Grid>
          <Grid
            container
            className={styles.centeredGrid}
            size={{
              xs: 12,
              lg: 12,
            }}
          >
            <Grid
              size={{
                xs: 12,
                lg: 12,
              }}
            >
              <Text variant="modalTitle" casing="sentence" className={genericSs.textCenter}>
                {title}
              </Text>
            </Grid>
            <Grid
              className={styles.centeredItem}
              size={{
                xs: 12,
                lg: 12,
              }}
            >
              <div
                data-cy="modal-warning-message"
                className={cn(genericSs.textCenter, styles.descriptionTitle)}
              >
                {warningMessage}
              </div>
            </Grid>
            {errorMessage && (
              <Grid
                className={styles.centeredItem}
                size={{
                  xs: 12,
                  lg: 12,
                }}
              >
                <div
                  data-cy="modal-error-message"
                  className={cn(genericSs.textCenter, styles.errorMessage)}
                >
                  {errorMessage}
                </div>
              </Grid>
            )}
            {inputComponent}
            {onConfirm && (
              <Grid
                className={styles.centeredItem}
                size={{
                  xs: 12,
                  lg: 12,
                }}
              >
                <Button
                  data-cy="modal-confirm-button"
                  className={styles.button}
                  onClick={onConfirm}
                  disabled={disabled}
                  isLoading={isLoading}
                >
                  {confirmText}
                </Button>
              </Grid>
            )}

            {onCancel && (
              <Grid
                className={styles.centeredItem}
                size={{
                  xs: 12,
                  lg: 12,
                }}
              >
                <Button
                  onClick={onCancel}
                  secondary
                  data-cy="modal-cancel-button"
                  className={styles.button}
                  disabled={disabled || isLoading}
                  isLoading={isLoadingCancel}
                >
                  {cancelText}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  )
}

export default WarningModal
